"use client";

import { storyblokEditable, ISbStoryData } from "@storyblok/react/rsc";
import { AlertCircle } from "lucide-react";
import Card from "@/components/Card";
import { GridStoryblok, MultilinkStoryblok } from "@/component-types-sb";
import { containerClassName, itemClassName } from "@/lib/grid-layout";

interface GenericGridClientProps {
  blok: GridStoryblok;
  initialQueryResult: {
    stories: ISbStoryData[];
    total: number;
  };
  startIndex: number;
}

function getAspectRatio(entry: ISbStoryData): number | undefined {
  const filename = entry.content?.image?.filename;
  if (filename) {
    const match = filename.match(/\/(\d+)x(\d+)\//);
    if (match) {
      const w = Number.parseInt(match[1]);
      const h = Number.parseInt(match[2]);
      if (w > 0) return h / w;
    }
  }
  return undefined;
}

type CardLink = string | Exclude<MultilinkStoryblok, { linktype?: "email" } | { linktype?: "asset" }>;

function buildCardProps(entry: ISbStoryData) {
  const getCardLink = (link: CardLink, fallback: string | undefined) => {
    if (typeof link === "string" && link) return link;
    if (link && typeof link === "object") {
      if (typeof link.cached_url === "string" && link.cached_url) return link.cached_url;
      if (typeof link.url === "string" && link.url) return link.url;
    }
    return fallback;
  };
  const cardLink = getCardLink(entry.content.link, entry.content.body ? entry.full_slug : undefined);
  const isEvent = entry.content.component == "event";
  const aspectRatio = isEvent ? getAspectRatio(entry) : undefined;

  return {
    key: entry.uuid,
    // Every card is one column wide, so no card requests the fullWidth image.
    fullWidth: false,
    aspectRatio,
    blok: {
      component: "card" as const,
      _uid: entry.uuid,
      variant: isEvent ? ("event" as const) : ("default" as const),
      title: entry.name,
      date: entry.content.date,
      description: entry.content.description,
      ...(entry.content.image?.filename ? { image: entry.content.image } : {}),
      ...(cardLink ? { link: cardLink } : {}),
    },
  };
}

const GenericGridClient: React.FC<GenericGridClientProps> = ({ blok, initialQueryResult, startIndex }) => {
  const entries = initialQueryResult?.stories || [];

  if (!initialQueryResult) {
    return (
      <div
        {...storyblokEditable(blok)}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 min-h-[200px] place-items-center"
      >
        <div className="col-span-full flex flex-col items-center justify-center text-red-500">
          <AlertCircle className="h-8 w-8 mb-2" />
          <p>Error loading entries</p>
          <p className="text-sm text-gray-500">Unknown error</p>
        </div>
      </div>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div {...storyblokEditable(blok)} className="p-6">
        <p>Momentan sind keine Einträge vorhanden!</p>
      </div>
    );
  }

  const slicedEntries = entries.slice(startIndex);
  const isEventGrid = blok.type === "event";

  return (
    <div
      {...storyblokEditable(blok)}
      className={containerClassName}
    >
      {slicedEntries.map((entry: ISbStoryData) => {
        const { key, ...props } = buildCardProps(entry);
        return (
          <div key={key} className={itemClassName(isEventGrid, slicedEntries.length)}>
            <Card {...props} />
          </div>
        );
      })}
    </div>
  );
};

export default GenericGridClient;
